import { env } from '@zopauk/nextjs-env';
import { buildBreadcrumbList } from '@/utils/schema';
import type { ArticleQuery } from '@/utils/dato/generated';

export type Breadcrumb = {
  label: string;
  // Path starting with a slash (e.g. `/blog`) or an absolute URL (e.g. the canonical URL of a page)
  href: string;
  // Only in the schema, not shown: the current page (the last item), whose title is the heading of the page itself
  hidden?: boolean;
};

type PageNode = {
  breadcrumbLabel?: string | null;
  parent?: PageNode | null;
  title: string;
  slug: string;
};

type PageForBreadcrumbs = { removeBreadcrumbs?: boolean | null; canonicalUrl?: string | null } & PageNode;

type CollectionNode = { hub?: HubNode | null; title: string; slug: string };

type Article = NonNullable<ArticleQuery['article']>;

type HubNode = { title: string; slug: string };

const HOME: Breadcrumb = { label: 'Home', href: '/' };
const HELP: Breadcrumb = { label: 'Help', href: '/help' };

const getBreadcrumbId = (pageUrl: string) => `${pageUrl}#breadcrumb`;

// Home > Blog > the article's primary category. The current page (the article) is the last item of the schema only,
// it is not shown in the trail (the same for all the other pages)
export const getArticleBreadcrumbs = ({ categories, title, slug }: Pick<Article, 'categories' | 'title' | 'slug'>) => {
  const category = categories?.[0];

  return [
    HOME,
    { label: 'Blog', href: '/blog' },
    ...(category?.slug && category?.title ? [{ href: `/blog/category/${category.slug}`, label: category.title }] : []),
    { href: `/blog/article/${slug}`, label: title },
  ];
};

// Home > the parents of the page in the CMS hierarchy (not in the URL) > the page itself (schema only).
// The label is the breadcrumb label of the page, or its title when the label is empty.
// No breadcrumbs (neither visible nor in the schema) for the home page and for the pages with "Remove breadcrumbs?"
export const getPageBreadcrumbs = (page: PageForBreadcrumbs): Breadcrumb[] => {
  if (page.slug === 'home' || page.removeBreadcrumbs) {
    return [];
  }

  const parents: Breadcrumb[] = [];

  for (let node = page.parent; node; node = node.parent) {
    parents.unshift({ label: node.breadcrumbLabel || node.title, href: `/${node.slug}` });
  }

  return [
    HOME,
    ...parents,
    {
      href: page.canonicalUrl || `/${page.slug}`,
      label: page.breadcrumbLabel || page.title,
    },
  ];
};

const getHubBreadcrumb = ({ title, slug }: HubNode): Breadcrumb => ({ href: `/help/${slug}`, label: title });

const getCollectionBreadcrumb = ({ title, slug }: CollectionNode): Breadcrumb => ({
  href: `/help/collection/${slug}`,
  label: title,
});

// Home > Help > the hub
export const getHubBreadcrumbs = (hub: { canonicalUrl?: string | null } & HubNode): Breadcrumb[] => [
  HOME,
  HELP,
  { ...getHubBreadcrumb(hub), href: hub.canonicalUrl || getHubBreadcrumb(hub).href },
];

// Home > Help > the hub of the collection > the collection itself
export const getCollectionBreadcrumbs = (collection: CollectionNode): Breadcrumb[] => [
  HOME,
  HELP,
  ...(collection.hub ? [getHubBreadcrumb(collection.hub)] : []),
  getCollectionBreadcrumb(collection),
];

// Home > Help > the hub > the first collection of the question > the question itself
export const getQuestionBreadcrumbs = ({
  canonicalUrl,
  collections,
  title,
  slug,
}: {
  collections?: CollectionNode[];
  canonicalUrl?: string | null;
  title: string;
  slug: string;
}): Breadcrumb[] => {
  const collection = collections?.[0];

  return [
    HOME,
    HELP,
    ...(collection?.hub ? [getHubBreadcrumb(collection.hub)] : []),
    ...(collection ? [getCollectionBreadcrumb(collection)] : []),
    { href: canonicalUrl || `/help/article/${slug}`, label: title },
  ];
};

const getAbsoluteUrl = (href: string) =>
  href.startsWith('http') ? href : `${env('NEXT_PUBLIC_WEB_GALLERIA_URL') ?? ''}${href}`;

// The schema is built from the same list that is rendered, so the visible trail and the markup always match.
// The last breadcrumb is the current page, so its URL (the canonical URL of the page) gives the `@id` of the list.
// There is no schema (undefined) without breadcrumbs
export const buildBreadcrumbSchema = (breadcrumbs: Breadcrumb[]) => {
  if (breadcrumbs.length === 0) {
    return undefined;
  }

  const items = breadcrumbs.map(({ label, href }) => ({ item: getAbsoluteUrl(href), name: label.trim() }));

  return buildBreadcrumbList(items, getBreadcrumbId(items.at(-1)?.item ?? ''));
};
